import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { useAiJobs } from "@/context/AiJobsContext";
import api, { API } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LayoutDashboard,
  Boxes,
  Workflow,
  SlidersHorizontal,
  GitCompareArrows,
  LogOut,
  FileSpreadsheet,
  FileText,
  ShieldCheck,
  Users,
  Radar as RadarIcon,
  BrainCircuit,
  Building2,
  Layers,
  Wrench,
  TrendingUp,
  ClipboardList,
  CreditCard,
  AlertTriangle,
  Bot,
  Menu,
  X,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clienti", icon: Building2, testid: "nav-clients" },
  { to: "/assistente", label: "Assistente AI", icon: Bot, testid: "nav-assistant" },
  { to: "/sopralluogo", label: "Sopralluogo", icon: ClipboardList, testid: "nav-wizard", editorOnly: true },
  { to: "/pipeline", label: "Pipeline", icon: TrendingUp, testid: "nav-pipeline", editorOnly: true },
  { to: "/readiness", label: "AI Readiness", icon: BrainCircuit, testid: "nav-readiness" },
  { to: "/frameworks", label: "Framework", icon: Layers, testid: "nav-frameworks", editorOnly: true },
  { to: "/tools", label: "Tool IT", icon: Boxes, testid: "nav-tools" },
  { to: "/compare", label: "Confronto", icon: GitCompareArrows, testid: "nav-compare" },
  { to: "/processes", label: "Processi", icon: Workflow, testid: "nav-processes" },
  { to: "/remediation-library", label: "Remediation", icon: Wrench, testid: "nav-remediation", editorOnly: true },
  { to: "/gap-analysis", label: "Gap Analysis", icon: RadarIcon, testid: "nav-gap" },
  { to: "/criteria", label: "Criteri", icon: SlidersHorizontal, testid: "nav-criteria" },
  { to: "/team", label: "Team", icon: Users, testid: "nav-team", adminOnly: true },
  { to: "/abbonamento", label: "Abbonamento", icon: CreditCard, testid: "nav-subscription", adminOnly: true },
];

const roleLabel = { admin: "Amministratore", member: "Membro", viewer: "Sola lettura" };
const PORTFOLIO = "__portfolio__";

function SubscriptionBanner() {
  const { isAdmin } = useAuth();
  const [sub, setSub] = useState(null);
  useEffect(() => {
    if (isAdmin) api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, [isAdmin]);
  if (!isAdmin || !sub?.has_plan) return null;
  let msg = null;
  let red = false;
  if (sub.status === "expired") { msg = "Abbonamento scaduto: rinnova il piano per continuare a operare."; red = true; }
  else if (sub.status === "suspended") { msg = "Abbonamento sospeso: contatta il gestore della piattaforma."; red = true; }
  else {
    const ref = sub.status === "trial" ? sub.trial_ends_at : sub.expires_at;
    if (ref) {
      const days = Math.ceil((new Date(ref) - Date.now()) / 86400000);
      if (days <= 7) {
        const when = days <= 0 ? "oggi" : days === 1 ? "domani" : `tra ${days} giorni`;
        msg = sub.status === "trial"
          ? `La prova gratuita del piano ${sub.plan.name} scade ${when}.`
          : `L'abbonamento ${sub.plan.name} scade ${when}. Contatta il gestore per il rinnovo.`;
      }
    }
  }
  if (!msg) return null;
  return (
    <div data-testid="subscription-banner" className={`px-4 sm:px-8 py-2.5 text-sm font-medium flex items-center gap-2 border-b ${red ? "bg-red-600 text-white border-red-700" : "bg-amber-100 text-amber-900 border-amber-300"}`}>
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <span>{msg}</span>
      <NavLink to="/abbonamento" className="underline underline-offset-2 ml-1 shrink-0" data-testid="banner-subscription-link">Dettagli</NavLink>
    </div>
  );
}

function ClientSwitcher({ onSelect }) {
  const { clients, activeClientId, setActiveClient } = useClient();
  return (
    <div className="px-4 py-3 border-b border-border">
      <div className="data-label mb-1.5">Cliente attivo</div>
      <Select
        value={activeClientId || PORTFOLIO}
        onValueChange={(v) => { setActiveClient(v === PORTFOLIO ? null : v); onSelect?.(); }}
      >
        <SelectTrigger data-testid="client-switcher" className="rounded-none h-9 w-full text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="rounded-none">
          <SelectItem value={PORTFOLIO}>Tutti i clienti (portfolio)</SelectItem>
          {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout, isAdmin, canEdit } = useAuth();
  const aiJobs = useAiJobs();
  const activeJobs = aiJobs?.activeJobs || [];
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const download = (path) => window.open(`${API}${path}`, "_blank");

  return (
    <div className="min-h-screen flex bg-background">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-border z-30 flex items-center justify-between px-4">
        <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="text-foreground p-1"><Menu className="w-6 h-6" /></button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary flex items-center justify-center"><ShieldCheck className="w-4 h-4 text-primary-foreground" /></div>
          <span className="font-heading font-black text-sm tracking-tight">ITASSESS</span>
        </div>
        <div className="w-8" />
      </div>

      {/* Overlay */}
      {mobileOpen && <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileOpen(false)} />}

      <aside className={`w-64 border-r border-border bg-white flex flex-col fixed h-screen z-50 transform transition-transform duration-200 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center justify-between px-6 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <div className="font-heading font-black text-sm tracking-tight leading-none">ITASSESS</div>
              <div className="data-label mt-0.5">Tool Evaluation</div>
            </div>
          </div>
          <button className="lg:hidden text-muted-foreground" onClick={() => setMobileOpen(false)}><X className="w-5 h-5" /></button>
        </div>

        <ClientSwitcher onSelect={() => setMobileOpen(false)} />

        <nav className="flex-1 py-4 overflow-y-auto">
          {NAV.filter((item) => (!item.adminOnly || isAdmin) && (!item.editorOnly || canEdit)).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-2 transition-colors duration-150 ${
                    isActive
                      ? "border-primary bg-secondary text-primary"
                      : "border-transparent text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {activeJobs.length > 0 && (
          <div data-testid="ai-jobs-indicator" className="border-t border-border px-4 py-2.5 flex items-center gap-2 text-xs text-primary bg-secondary/60">
            <span className="w-2 h-2 bg-primary animate-pulse shrink-0" />
            AI in elaborazione ({activeJobs.length})… verrai avvisato
          </div>
        )}

        <div className="border-t border-border p-4 space-y-2">
          <div className="data-label px-2">Export</div>
          <button
            data-testid="export-excel-btn"
            onClick={() => download("/export/tools.xlsx")}
            className="w-full flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors duration-150"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel Assessment
          </button>
          <button
            data-testid="export-pdf-btn"
            onClick={() => download("/export/report.pdf")}
            className="w-full flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors duration-150"
          >
            <FileText className="w-4 h-4" /> Report PDF
          </button>
        </div>

        <div className="border-t border-border p-4 flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 object-cover" />
          ) : (
            <div className="w-8 h-8 bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold font-mono">
              {(user?.name || user?.email || "?").slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name || "Utente"}</div>
            <div className="text-xs text-muted-foreground truncate">{roleLabel[user?.role] || user?.role}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="text-muted-foreground hover:text-destructive transition-colors duration-150"
            title="Esci"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      <main className="flex-1 lg:ml-64 min-h-screen pt-14 lg:pt-0"><SubscriptionBanner />{children}</main>
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="min-h-16 border-b border-border bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-8 py-3 sm:py-0 sm:sticky sm:top-0 z-10">
      <div>
        <h1 className="font-heading font-black text-lg sm:text-xl tracking-tight leading-none">{title}</h1>
        {subtitle && <div className="data-label mt-1">{subtitle}</div>}
      </div>
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">{children}</div>
    </div>
  );
}
